import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getValidToken } from "../../utils/tokenManager";
import axios from "axios";
import Sidebar from "../common/Sidebar";
import Button from "../common/Button";
import SendCommentModal from "./commonSections.jsx/SendCommentModal";
import logoIcon from "../../assets/common/logo-icon.svg";
import logout from "../../assets/common/logout.svg";
import finish from "../../assets/agent/End Day Icon.svg";

const Achievement = () => {
  const [dashboardData, setDashboardData] = useState("");
  const [comment, setComment] = useState("");
  const [commentModal, setCommentModal] = useState(false);

  const { t } = useTranslation();
  const user = sessionStorage.getItem("userName");
  const clientName = sessionStorage.getItem("clientName");
  const [sidebarPinned, setSidebarPinned] = useState(() => {
    const saved = localStorage.getItem("agentSidebarPinned");
    return saved === "true";
  });

  const MAX_CHARS = 1000;
  const charCount = comment?.length ?? 0;
  const isOverLimit = charCount > MAX_CHARS;
  const helperText = isOverLimit
    ? `${t("agent.achieve.helperText.left")} ${charCount - MAX_CHARS} ${t(
        "agent.achieve.helperText.right"
      )}`
    : "";
  const showLimit = `${charCount}/${MAX_CHARS}`;

  const counterTextClass = isOverLimit ? "text-red-500" : "text-gray-600";

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const accessToken = await getValidToken();
        const today = new Date();
        const dateoftoday = `${today.getFullYear()}-${String(
          today.getMonth() + 1
        ).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;

        const config = {
          method: "get",
          url: `${process.env.REACT_APP_APIBASEURL}/api/dashboard/${dateoftoday}/${dateoftoday}?utc_offset_minutes=${today.getTimezoneOffset()}`,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        };

        const response = await axios(config);
        setDashboardData(response.data);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        if (error.response) {
          // Server responded with a status outside 2xx
          console.error("Server Response:", error.response.data);
        } else if (error.request) {
          // Request was made but no response received
          console.error("No Response Received:", error.request);
        } else {
          // Something else happened
          console.error("Error:", error.message);
        }
      }
    };

    fetchDashboardData();
  }, []);

  // Metrics for the goals card
  const goals = [
    {
      label: t("agent.agentDashboard.cards.totalRemains"),
      value: dashboardData.total_contacts_remaining,
      color: "text-[#0B3C88]",
    },
    {
      label: t("agent.agentDashboard.cards.totalCalls"),
      value: dashboardData.total_calls_made,
      color: "text-[#0B3C88]",
    },
    {
      label: t("agent.agentDashboard.cards.callsAnswered"),
      value: dashboardData.total_calls_answered,
      color: "text-[#0B3C88]",
    },
    {
      label: t("agent.agentDashboard.cards.leads"),
      value: dashboardData.total_leads,
      color: "text-red-500",
    },
    {
      label: t("agent.agentDashboard.cards.totalBooked"),
      value: dashboardData.total_meetings_booked,
      color: "text-[#0B3C88]",
    },
    {
      label: t("agent.agentDashboard.cards.totalAttended"),
      value: dashboardData.total_meetings_confirmed_attended,
      color: "text-[#0B3C88]",
    },
  ];

  const handleSendComment = async () => {
    setCommentModal(true);
  };

  return (
    <div className="flex min-h-screen bg-secondaryBg">
      <Sidebar
        logoIcon={logoIcon}
        logout={logout}
        finish={finish}
        labelClassName="bg-secondary"
        onToggle={(pinned) => setSidebarPinned(pinned)}
      />
      <div className={`flex-1 flex flex-col px-4 sm:px-8 py-6 md:py-10 transition-all duration-300 ${
        sidebarPinned ? "ml-[250px]" : "ml-[80px]"
      }`}>
        <div className="max-w-6xl mx-auto w-full">
          <span className="text-lg md:text-xl text-[#0A1E4C] font-medium">
            {t("dashboard.greeting", { name: user })}
          </span>
          <h1 className="text-[30px] font-bold text-primary">
            {t("agent.achieve.head")}
          </h1>
          <div className="relative flex items-center mb-5">
            <span className="absolute left-0 w-1 h-1 bg-primary rounded-full -ml-1"></span>
            <hr className="flex-1 border-t-2 border-primary" />
            <span className="absolute right-0 w-1 h-1 bg-primary rounded-full -mr-1"></span>
          </div>
          <div className="mb-4">
            <p className="text-xl text-primary font-bold">{clientName}</p>
          </div>
          <div>
            {/* Your goals card */}
            <div className="w-full mt-12 mb-16">
              <div
                className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2"
                style={{
                  clipPath: "polygon(0 0, 100% 30%, 100% 100%, 0 100%)",
                }}
              >
                {goals.map((goal, idx) => {
                  const backgrounds = [
                    "bg-primary",
                    "bg-[#082D65]",
                    "bg-secondary",
                    "bg-[#FFBD67]",
                    "bg-tertiary",
                    "bg-[linear-gradient(160deg,#FF6A00_0%,#FF9900_100%)]",
                  ];
                  const bgClass = backgrounds[idx % backgrounds.length];
                  return (
                    <div
                      key={goal.label}
                      className="flex flex-col items-center"
                    >
                      <div
                        className={`w-full h-24 ${bgClass} text-white flex items-center justify-center pt-10`}
                      >
                        <span className="text-lg font-bold">
                          {goal?.value ?? "-"}
                        </span>
                      </div>
                      <div className="mt-2 text-center">
                        <span className="text-sm text-icon font-medium leading-tight">
                          {goal.label}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Comments section */}
            <div className="flex flex-col w-full">
              <label htmlFor="comments" className="text-xl font-medium mb-2">
                {t("agent.achieve.comment")}
              </label>
              <div className="pointer-events-none">
                <div
                  id="comments-counter"
                  aria-live="polite"
                  className={`text-sm font-medium ${counterTextClass}`}
                >
                  {helperText}
                </div>
                {isOverLimit && (
                  <p className="text-sm text-red-500 mr-auto mb-1">
                    {t("agent.achieve.showLimit.left")} {MAX_CHARS}{" "}
                    {t("agent.achieve.showLimit.right")}.
                  </p>
                )}
              </div>

              <div className="relative">
                <textarea
                  id="comments"
                  aria-describedby="comments-counter"
                  className={`bg-lightBlue border border-secondary rounded-md p-3 min-h-[145px] mb-4 focus:outline-none focus:ring-2 resize-none w-full`}
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                />

                <div className="absolute right-3 bottom-7 pointer-events-none">
                  <div
                    id="comments-counter"
                    aria-live="polite"
                    className={`text-sm font-medium text-gray-600 bg-white bg-opacity-0 px-2`}
                  >
                    {showLimit}
                  </div>
                </div>
              </div>

              <div className="flex justify-end items-center gap-3">
                <Button
                  variant="primary"
                  className="py-2 text-base font-semibold w-[200px]"
                  onClick={handleSendComment}
                  disabled={isOverLimit || comment.trim().length === 0}
                >
                  {t("button.send")}
                </Button>
              </div>

              <SendCommentModal
                open={commentModal}
                onClose={() => setCommentModal(false)}
                dashboardData={dashboardData}
                comment={comment}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Achievement;
